import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { Role } from '../auth/roles';
import { RoleGuard } from '../auth/RoleGuard';
import { Widget } from '../components/dashboard/Widget';
import { RecentOrdersPanel, type RecentOrderRow } from '../components/dashboard/RecentOrdersPanel';
import { StockAlertsPanel, type StockAlertRow } from '../components/dashboard/StockAlertsPanel';
import { useNotificationStore } from '../stores/notificationStore';
import apiClient from '../api/client';
import { Permission } from '../auth/permissions';
import { usePermission } from '../auth/usePermission';
import { formatEUR } from '../lib/format';
import { PageHeader } from '../components/ui/PageHeader';

/**
 * Screen 6 — Adaptive dashboard.
 * Widget list comes from /me/capabilities (backend), so the *what* is server-driven
 * (ZERO frontend logic). The *how* it's rendered is layout glue only.
 * Auto-refreshes every 60 s as per Part F spec.
 */
export default function Dashboard() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const caps = useCapabilitiesStore((s) => s.data);
  const widgetKeys = caps?.navigation.dashboard ?? [];
  return (
    <div className="space-y-6">
      <PageHeader
        title={t('dashboard.title')}
        description={t('dashboard.subtitle', {
          role: t(`roles.${user?.role}`, { defaultValue: user?.role ?? '' }),
          count: widgetKeys.length,
        })}
      />

      {/* Role-grouped widget grids — each role's spec is in Section 9 / Part F Screen 6. */}
      <RoleGuard roles={[Role.OWNER, Role.MANAGER]}>
        <OwnerManagerWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.WAREHOUSE_KEEPER]}>
        <WarehouseWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.PURCHASER]}>
        <PurchaserWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.COUNTER_AGENT]}>
        <CounterWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.ACCOUNTING]}>
        <AccountingWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.B2B_OWNER, Role.B2B_BUYER, Role.B2B_VIEWER]}>
        <B2bWidgets />
      </RoleGuard>

      <RoleGuard roles={[Role.ADMIN]}>
        <AdminWidgets />
      </RoleGuard>
    </div>
  );
}

// Widget data is fetched lazily by each block — each fetches only its own
// dependencies (report KPIs, stock alerts, orders …) so a single missing endpoint
// doesn't blank the whole dashboard.

function useAutoRefresh<T>(fetcher: () => Promise<T>, deps: unknown[] = []): T | null {
  const [value, setValue] = useState<T | null>(null);
  useEffect(() => {
    let alive = true;
    const tick = () =>
      fetcher()
        .then((v) => alive && setValue(v))
        .catch(() => {});
    tick();
    const t = setInterval(tick, 60_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return value;
}

interface DashboardPayload {
  revenueToday: { revenue_ht: string; revenue_ttc: string; invoice_count: number };
  marginToday: { revenue_ht: number; margin_ht: number; margin_pct: number };
  ordersToday: number;
  activeStockAlerts: number;
  unpaidInvoices: { count: number; amountDue: number; overdue: number };
}

function OwnerManagerWidgets() {
  const { t } = useTranslation();
  const canSeeMargin = usePermission(Permission.MARGIN_VIEW);
  // Read the badge count from the same store the appbar bell uses, and drive it off this
  // screen's own 60 s tick so the tile is right even on a layout without the bell.
  const unreadNotifications = useNotificationStore((s) => s.unread);
  const refreshNotifications = useNotificationStore((s) => s.refresh);
  useAutoRefresh(refreshNotifications, [refreshNotifications]);

  // One call feeds the whole tile row (GET /reports/dashboard is OWN/MGR/ACC/ADM). The two
  // panels below keep their own fetches so a failing report doesn't blank the lists, and
  // vice versa.
  const kpis = useAutoRefresh<DashboardPayload>(() =>
    apiClient.get<{ data: DashboardPayload }>('/reports/dashboard').then((r) => r.data.data)
  );
  const alerts = useAutoRefresh<StockAlertRow[]>(() =>
    apiClient.get<StockAlertRow[]>('/stock-alerts?status=ACTIVE').then((r) => r.data ?? [])
  );
  const orders = useAutoRefresh<RecentOrderRow[]>(() =>
    apiClient.get<RecentOrderRow[]>('/orders').then((r) => (Array.isArray(r.data) ? r.data : []))
  );

  const unpaid = kpis?.unpaidInvoices;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <Widget
          title={t('dashboard.stockAlerts')}
          value={alerts?.length ?? '…'}
          tone={alerts && alerts.length > 0 ? 'critical' : 'default'}
        />
        <Widget
          title={t('dashboard.margins')}
          // MARGIN_VIEW is what separates a role that may see cost from one that may not
          // (WP-9 strips it from COUNTER/B2B) — never render the figure without it.
          value={!canSeeMargin ? '—' : kpis ? `${kpis.marginToday.margin_pct.toFixed(1)} %` : '…'}
          detail={
            !canSeeMargin
              ? t('dashboard.marginsRequires')
              : kpis
                ? t('dashboard.marginsDetail', { amount: formatEUR(kpis.marginToday.margin_ht) })
                : undefined
          }
        />
        <Widget
          title={t('dashboard.revenueDay')}
          value={kpis ? formatEUR(kpis.revenueToday.revenue_ttc) : '…'}
          detail={
            kpis
              ? t('dashboard.invoicesIssuedToday', { count: kpis.revenueToday.invoice_count })
              : undefined
          }
        />
        <Widget title={t('dashboard.ordersToday')} value={kpis?.ordersToday ?? '…'} />
        <Widget
          title={t('dashboard.unpaidInvoices')}
          value={unpaid ? unpaid.count : '…'}
          tone={unpaid && unpaid.overdue > 0 ? 'critical' : 'default'}
          detail={
            unpaid
              ? t('dashboard.unpaidDetail', {
                  amount: formatEUR(unpaid.amountDue),
                  overdue: unpaid.overdue,
                })
              : undefined
          }
        />
        <Widget
          title={t('dashboard.notifications')}
          value={unreadNotifications}
          detail={unreadNotifications > 0 ? t('dashboard.unread') : t('dashboard.allCaughtUp')}
        />
      </div>

      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <RecentOrdersPanel rows={orders} loading={orders === null} />
        </div>
        <StockAlertsPanel rows={alerts} loading={alerts === null} />
      </div>
    </div>
  );
}

function WarehouseWidgets() {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
      <Widget
        title={t('dashboard.expectedDeliveries')}
        value="—"
        detail={t('dashboard.expectedDeliveriesDetail')}
      />
      <Widget
        title={t('dashboard.ordersToPrepare')}
        value="—"
        detail={t('dashboard.ordersToPrepareDetail')}
      />
      <Widget
        title={t('dashboard.partialBaskets')}
        value="—"
        detail={t('dashboard.partialBasketsDetail')}
      />
      <Widget title={t('dashboard.readyBaskets')} value="—" />
    </div>
  );
}

function PurchaserWidgets() {
  const { t } = useTranslation();
  const alerts = useAutoRefresh<{ length: number }>(() =>
    apiClient.get('/stock-alerts?status=ACTIVE').then((r) => ({ length: (r.data ?? []).length }))
  );
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Widget
        title={t('dashboard.activeAlerts')}
        value={alerts?.length ?? '…'}
        tone={alerts && alerts.length > 0 ? 'critical' : 'default'}
      />
      <Widget title={t('dashboard.posInTransit')} value="—" />
      <Widget title={t('dashboard.lateDeliveries')} value="—" />
    </div>
  );
}

function CounterWidgets() {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Widget
        title={t('dashboard.todaysSales')}
        value="—"
        detail={t('dashboard.todaysSalesDetail')}
      />
      <Widget
        title={t('dashboard.registerStatus')}
        value="—"
        detail={t('dashboard.registerStatusDetail')}
      />
      <Widget title={t('dashboard.myPendingDiscounts')} value="—" />
    </div>
  );
}

function AccountingWidgets() {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Widget title={t('dashboard.invoicesToday')} value="—" />
      <Widget title={t('dashboard.unsentInvoices')} value="—" />
      <Widget title={t('dashboard.creditNotesPending')} value="—" />
    </div>
  );
}

interface B2bSummary {
  orders: { total: number; open: number };
  credit: { limit: number; outstandingBalance: number; reserved: number; available: number };
}

function B2bWidgets() {
  const { t } = useTranslation();
  const summary = useAutoRefresh<B2bSummary>(() =>
    apiClient.get<B2bSummary>('/b2b/summary').then((r) => r.data)
  );
  const credit = summary?.credit;
  const overLimit = credit ? credit.outstandingBalance + credit.reserved > credit.limit : false;
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Widget
        title={t('dashboard.myOrders')}
        value={summary ? summary.orders.total : '…'}
        detail={
          summary
            ? t('dashboard.myOrdersDetail', { open: summary.orders.open })
            : t('dashboard.ownCompanyOnly')
        }
      />
      <Widget
        title={t('dashboard.outstandingBalance')}
        value={credit ? formatEUR(credit.outstandingBalance) : '…'}
        detail={t('dashboard.outstandingBalanceDetail')}
      />
      <Widget
        title={t('dashboard.availableCredit')}
        value={credit ? formatEUR(credit.available) : '…'}
        tone={overLimit ? 'critical' : 'default'}
        detail={
          credit
            ? t('dashboard.availableCreditDetail', {
                limit: formatEUR(credit.limit),
                reserved: formatEUR(credit.reserved),
              })
            : undefined
        }
      />
    </div>
  );
}

function AdminWidgets() {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
      <Widget title={t('dashboard.system')} value="UP" />
      <Widget title={t('dashboard.users')} value="—" />
      <Widget title={t('dashboard.supplierApiHealth')} value="—" />
    </div>
  );
}
